import { FieldError, FieldLabel, useErrorId, useLabelId } from '@calendly/form/parts';
import { useDropdown } from '@calendly/ui/components/dropdown';
import { Loader } from '@calendly/ui/components/loader';
import { ControlUI, OptionsUI, SelectUI } from '@calendly/ui/components/select';
import classNames from 'classnames';
import { useEffect, useRef, useState, forwardRef } from 'react';
import { useDebounce } from 'hooks/useDebounce';
import { SelectProvider } from './context';
const styledInputCss = "s1ac0c6i";
const StyledInput = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'input';
  return <Component ref={ref} {...restProps} className={classNames(styledInputCss, props.className)} />;
});
const wrapperCss = "w1dit3m0";
const Wrapper = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(wrapperCss, props.className)} />;
});
const overlayDisabledCss = "odfr0c1";
const overlayMutedCss = "o1qdo8zz";
const overlayCss = "ow52yrw";
const Overlay = forwardRef((props, ref) => {
  const {
    disabled,
    muted,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(overlayCss, {
    [overlayDisabledCss]: disabled,
    [overlayMutedCss]: muted
  }, props.className)} />;
});
const loaderContainerCss = "l117muzd";
const LoaderContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(loaderContainerCss, props.className)} />;
});
const OverlayContent = ({
  isExpanded,
  placeholder,
  selectedOption,
  isLoading,
  disabled
}) => {
  if (isLoading && !selectedOption) return <Overlay disabled={disabled}><LoaderContainer><Loader size="small" /></LoaderContainer></Overlay>;
  if (!isExpanded && !selectedOption && placeholder) return <Overlay disabled={disabled} muted>{placeholder}</Overlay>;
  if (isExpanded || !selectedOption) return null;
  return <Overlay disabled={disabled}>{selectedOption}</Overlay>;
};
const optionsContainerCss = "ogyedbq";
const OptionsContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(optionsContainerCss, props.className)} />;
});
export const SearchableSelectField = ({
  name,
  className = null,
  label = null,
  labelledBy = null,
  proTip = null,
  required = false,
  displayValue = null,
  options = [],
  onChange,
  onSearch,
  placeholder = null,
  emptyPlaceholder = null,
  error = null,
  onFocus = null,
  children,
  isLoading,
  disabled,
  ...props
}) => {
  const controlId = `${name}_controls`;
  const [toggleDropdown, Dropdown, isExpanded] = useDropdown();
  const [searchQuery, setSearchQuery] = useState('');
  const inputRef = useRef(null);
  const errorId = useErrorId({
    error
  });
  const labelId = useLabelId({
    label
  });
  const debouncedQuery = useDebounce(searchQuery, 300);
  useEffect(() => {
    if (onSearch && isExpanded) onSearch(debouncedQuery);
  }, [onSearch, debouncedQuery, isExpanded]);
  useEffect(() => {
    if (!isExpanded) {
      if (onSearch && searchQuery !== '') onSearch('');
      setSearchQuery('');
    }
  }, [isExpanded, setSearchQuery, onSearch, searchQuery]);
  useEffect(() => {
    if (isLoading || !inputRef.current) return;
    if (isExpanded) {
      inputRef.current.focus();
    } else {
      inputRef.current.blur();
    }
  }, [isExpanded, inputRef, isLoading]);
  const onInputChange = e => {
    if (!isExpanded) toggleDropdown(e);
    setSearchQuery(e.target.value);
  };
  const handleFocus = () => {
    if (onFocus) onFocus();
  };
  const handleClick = e => {
    if (!isExpanded) toggleDropdown(e);
  };
  const getPlaceholder = () => {
    if (isLoading) return null;
    if (isExpanded) return placeholder;
    if (!displayValue) return emptyPlaceholder || placeholder;
    return null;
  };
  const handleButtonDown = e => {
    if (!isExpanded && e.code === 'Enter') {
      toggleDropdown(e);
      e.preventDefault();
    }
  };
  const invalid = Boolean(error);
  return <SelectProvider value={{
    options,
    onChange,
    invalid
  }}>
      <div className={className}>
        <FieldLabel text={label} proTip={proTip} required={required} id={labelId} />
        <SelectUI>
          <ControlUI as="div" {...props} expanded={isExpanded} invalid={invalid} disabled={disabled} tabIndex="0" onKeyDown={handleButtonDown} onClick={handleClick} role="combobox" aria-labelledby={labelId || labelledBy} aria-expanded={isExpanded} aria-describedby={errorId} aria-controls={controlId} aria-invalid={Boolean(error)} name={name}>
            <Wrapper>
              {!isLoading && isExpanded && <StyledInput name={`${name}_search`} ref={inputRef} aria-label="Search" role="searchbox" onChange={onInputChange} value={searchQuery} placeholder={getPlaceholder()} autoComplete="off" autoCorrect="off" disabled={disabled} onFocus={handleFocus}
            // prevent LastPass extension from filling this input
            data-lpignore="true" />}
              <OverlayContent isExpanded={isExpanded} isLoading={isLoading} selectedOption={displayValue} disabled={disabled} placeholder={placeholder} />
            </Wrapper>
          </ControlUI>
          <Dropdown>
            <OptionsContainer>
              <OptionsUI onClose={toggleDropdown} id={controlId} role="listbox" aria-label="Options" invalid={invalid}>
                {children}
              </OptionsUI>
            </OptionsContainer>
          </Dropdown>
        </SelectUI>
        <FieldError text={error} id={errorId} />
      </div>
    </SelectProvider>;
};

require("./searchable-select-field.wyw-in-js.css!=!../../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./searchable-select-field.js");